import { MainContainer } from "@flow/frontend/member/components/MainContainer";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { DonationTrackingConfig, DonationTrackingTotals } from "../../common/action_types";
import { JourneyGuid } from "@flow/common/types";
import { memberTheme } from "@flow/frontend/member/memberTheme";
import { DonationTrackingType } from "../../common/types";
import { RecentDonations } from "./RecentDonations";

export function DonationProgressBar(props: { total: number; target: number }) {
  // we want to ensure that the target percent is between 5 and 100 so it doesn't look visually weird
  const targetPercent = Math.max(5, Math.min(100, (100 * props.total) / props.target));

  return (
    <LinearProgress
      variant="determinate"
      value={targetPercent}
      sx={{
        width: "100%",
        height: "8px",
        borderRadius: "0",
      }}
      aria-label="Donations raised"
      aria-valuenow={props.total}
      aria-valuemin={0}
      aria-valuemax={props.target}
    />
  );
}

export function DonationTracker(props: {
  config: DonationTrackingConfig;
  totals: DonationTrackingTotals;
  journey_guid: JourneyGuid;
}) {
  const donation_total = getDonationTotal(props.config, props.totals);

  return (
    <MainContainer sx={{ marginBottom: "24px" }}>
      {props.config.title && (
        <Typography
          sx={{ typography: { xs: "subtitle1", md: "h5", marginBottom: "12px" } }}
          color={memberTheme.palette.primary.main}
        >
          {props.config.title}
        </Typography>
      )}

      <Stack spacing={2}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h4" color="primary">
            £{donation_total.toLocaleString()}
          </Typography>
          <Typography variant="body2">{getDonationTrackingProgressMessage(props.config)}</Typography>
        </Stack>
        <DonationProgressBar total={donation_total} target={props.config.target_amount} />
        <Box sx={{ height: "2rem" }}>
          <RecentDonations journeyGuid={props.journey_guid} />
        </Box>
      </Stack>
    </MainContainer>
  );
}

export const getDonationTotal = (config: DonationTrackingConfig, data: DonationTrackingTotals): number => {
  if (config.target_frequency === DonationTrackingType.ONE_OFF) {
    return data.oneoff_donation_total;
  } else if (config.target_frequency === DonationTrackingType.REGULAR) {
    return data.regular_donation_total;
  } else if (config.target_frequency === DonationTrackingType.ALL) {
    return data.donation_total;
  } else {
    // Default to ALL
    return data.donation_total;
  }
};

export const getDonationTrackingProgressMessage = (config: DonationTrackingConfig): string => {
  const DONATION_TARGET_AMOUNT_TAG = "{ target_amount }";
  return config.progress_message.replace(DONATION_TARGET_AMOUNT_TAG, config.target_amount.toLocaleString());
};
